/**
 * DraftPage — dedicated screen for the live deck-builder draft.
 *
 * Reached by navigating to `/deck-builder-draft?game=<id>&role=<home|away>`
 * once the lobby's per-player Ready Up has fired and the server has
 * initialized the draft pool. When the draft completes, navigates back to
 * `/online-setup` so the roster-creation step takes over.
 *
 * Two formats:
 *   • rotisserie — alternating picks from a shared pool, grouped by rarity.
 *   • pack — Deal 4, Pick 2, Burn 2. Both players see all 4 cards face-up
 *     simultaneously, tentatively select one (first-clicker locks the card
 *     from the opponent), then Ready Up to commit. When both committed, the
 *     remaining 2 are permanently burned; the next pack opens with the
 *     opposite first-picker.
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Loader2, Check, Lock, Flame, Wifi, WifiOff, ScrollText, ChevronLeft, ChevronRight } from "lucide-react";

import { useGameSocket } from "@/hooks/useGameSocket";
import {
  DraftCardChip,
  DRAFT_RARITY_STYLE,
  draftRarityStyle,
} from "@/components/draft/DraftCardFront";
import { DRAFT_FORMATS } from "@/constants/draft";
import { Nav } from "@/components/lobby/primitives";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── Constants / styling ────────────────────────────────────────────────── */

// DRAFT_FORMATS now lives in @/constants/draft (imported below).

// Card front + rarity styling now live in @/components/draft/DraftCardFront —
// imported at the top of this file. Both DraftPage and the lobby's
// post-draft review modal share the same primitives.

/* ─── Pick-clock countdown ───────────────────────────────────────────────── */

function DraftPickClock({ startedAt, duration }) {
  const deadline = startedAt && duration ? startedAt + duration * 1000 : null;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!deadline) return;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [deadline]);
  if (!deadline) return null;
  const remainingMs = Math.max(0, deadline - now);
  const secs = Math.ceil(remainingMs / 1000);
  const urgent = secs <= 5;
  return (
    <span className={`text-xs font-black tabular-nums px-2 py-1 rounded-lg border ${
      urgent
        ? "bg-red-500/15 border-red-500/40 text-red-300 animate-pulse"
        : "bg-slate-800 border-slate-700 text-slate-300"
    }`}>
      {secs}s
    </span>
  );
}

/* ─── Pool grid (rotisserie) ─────────────────────────────────────────────── */

function PoolGrid({ pool, catalog, disabled, onPick, grouped }) {
  const items = pool.map(id => catalog[id]).filter(Boolean);
  if (items.length === 0) {
    return <p className="text-xs text-slate-600 italic text-center py-8">Pool empty.</p>;
  }
  if (!grouped) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5 max-h-[440px] overflow-y-auto pr-1 auto-rows-fr">
        {items.map(c => <DraftCardChip key={c.id} card={c} disabled={disabled} onPick={onPick} />)}
      </div>
    );
  }
  const order = ["iconic", "legendary", "exclusive", "rare", "uncommon", "common", "junk_wax"];
  const groups = order
    .map(r => ({ rarity: r, cards: items.filter(c => c.rarity === r) }))
    .filter(g => g.cards.length > 0);
  return (
    <div className="space-y-3 max-h-[440px] overflow-y-auto pr-1">
      {groups.map(({ rarity, cards }) => {
        const rar = draftRarityStyle(rarity);
        return (
          <div key={rarity}>
            <p className="text-[10px] font-black uppercase tracking-widest mb-1 text-slate-400">
              {rar.label} <span className="text-slate-600">· {cards.length}</span>
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5 auto-rows-fr">
              {cards.map(c => <DraftCardChip key={c.id} card={c} disabled={disabled} onPick={onPick} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Pack draft — back→front flip card slot ─────────────────────────────── */

// A single card slot in the pack. Mounts face-down (`rotateY(180)`), animates
// in with a stagger, then flips its inner content at the rotation's midpoint
// so the back never reads mirrored. Ancestor remount via `packSeq` key fires
// this fresh for every new pack.
//
// `badge` (optional) overlays a status pill on top of the chip — "Yours",
// "Locked", "Picked", "Burned" — based on the pack-draft state.
function PackCard({ card, index, status, badge, dim, onSelect, disabled }) {
  const [flipped, setFlipped] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setFlipped(true), 450 + index * 130);
    return () => clearTimeout(t);
  }, [index]);

  const animateOut = status === "out";
  return (
    // `h-full` chain (motion.div → inner transform → chip/back) lets the grid
    // row's `auto-rows-fr` height flow through to the actual card markup, so
    // every card in a row resolves to identical dimensions even when their
    // descriptions vary in length.
    <motion.div
      className="h-full"
      initial={{ rotateY: 180, opacity: 0, y: 14 }}
      animate={animateOut
        ? { rotateY: 0, opacity: 0, y: -24, scale: 0.92 }
        : { rotateY: 0, opacity: 1, y: 0 }}
      transition={animateOut
        ? { duration: 0.6, ease: "easeIn" }
        : { delay: 0.15 + index * 0.13, duration: 0.55, ease: "easeInOut" }}
      style={{ transformStyle: "preserve-3d", perspective: 900 }}
    >
      <div className="h-full" style={{ transform: flipped ? "rotateY(0deg)" : "rotateY(180deg)" }}>
        {flipped ? (
          <DraftCardChip card={card} onPick={onSelect} disabled={disabled} badge={badge} dim={dim} />
        ) : (
          // Back-of-card uses the same aspect + min-h as DraftCardChip so the
          // flip transition stays in-place (no jump in card size). `h-full`
          // mirrors the front so back-face cards also stretch with the row.
          <div className="w-full h-full aspect-[5/7] min-h-[180px] rounded-lg border-2 border-amber-500/40 overflow-hidden shadow-md bg-slate-900">
            <img src="/card-back.png" alt="" className="w-full h-full object-cover" draggable={false} />
          </div>
        )}
      </div>
    </motion.div>
  );
}

/* ─── Pack draft area ────────────────────────────────────────────────────── */

// Top half of the pack-draft screen: the 4 face-up cards with per-card
// status overlays. Status is computed from the four pack-state fields the
// server broadcasts (selections + commit flags + pack status).
function PackBoard({
  pack, packSeq, packStatus, packBurned, lastPickPicks,
  selectionHome, selectionAway, committedHome, committedAway,
  role, catalog, onSelect,
}) {
  // During `picking`, items = current pack. During `burn_reveal`, we want
  // to render the same 4 slots — picked cards stay (or animate elsewhere)
  // and burned cards stay with a fading-out animation.
  const renderingReveal = packStatus === "burn_reveal";
  const sourceIds = renderingReveal
    ? [
        ...(lastPickPicks?.home ? [lastPickPicks.home] : []),
        ...(lastPickPicks?.away ? [lastPickPicks.away] : []),
        ...(packBurned || []),
      ]
    : pack;

  const oppRole = role === "home" ? "away" : "home";
  const myselection = role === "home" ? selectionHome : selectionAway;
  const oppSelection = role === "home" ? selectionAway : selectionHome;
  const myCommitted = role === "home" ? committedHome : committedAway;

  // Per-card badge resolver — drives the visual status of each slot.
  const resolveBadge = (cardId) => {
    if (renderingReveal) {
      if (cardId === lastPickPicks?.home) {
        return { label: "Home pick", classes: "bg-emerald-500/30 border-emerald-500/60 text-emerald-200" };
      }
      if (cardId === lastPickPicks?.away) {
        return { label: "Away pick", classes: "bg-emerald-500/30 border-emerald-500/60 text-emerald-200" };
      }
      return { label: "Burned", classes: "bg-red-500/30 border-red-500/60 text-red-200" };
    }
    if (cardId === myselection) {
      return { label: "Yours", classes: "bg-amber-500/30 border-amber-500/60 text-amber-100" };
    }
    if (cardId === oppSelection) {
      return { label: "Locked", classes: "bg-red-500/30 border-red-500/60 text-red-200" };
    }
    return null;
  };

  const resolveDim = (cardId) => {
    if (renderingReveal) {
      // Burned cards fade; picked cards stay vivid.
      return packBurned?.includes(cardId);
    }
    // During picking, lock the opponent's selection visually.
    return cardId === oppSelection;
  };

  const resolveStatus = (cardId) => {
    if (renderingReveal && packBurned?.includes(cardId)) return "out";
    return "in";
  };

  const resolveDisabled = (cardId) => {
    if (renderingReveal) return true;
    if (myCommitted) return true;            // already committed — locked
    if (cardId === oppSelection) return true; // opponent has it
    return false;
  };

  return (
    <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 space-y-3">
      <div className="flex items-baseline justify-between">
        <p className="text-[10px] font-black uppercase tracking-widest text-amber-400">
          Pack #{packSeq || 1}
        </p>
        <p className="text-[10px] text-slate-500">
          {renderingReveal
            ? "Reveal — next pack opens shortly"
            : "Pick a card · Ready Up to commit"}
        </p>
      </div>
      <div key={`${packSeq}-${packStatus}`} className="grid grid-cols-2 sm:grid-cols-4 gap-3 auto-rows-fr">
        {sourceIds.map((cid, i) => {
          const c = catalog[cid] || { id: cid, name: cid, rarity: "unknown" };
          return (
            <PackCard
              key={cid}
              card={c}
              index={i}
              status={resolveStatus(cid)}
              badge={resolveBadge(cid)}
              dim={resolveDim(cid)}
              disabled={resolveDisabled(cid)}
              onSelect={() => onSelect(cid === myselection ? null : cid)}
            />
          );
        })}
      </div>
    </div>
  );
}

/* ─── Pack draft — per-player Ready Up row ───────────────────────────────── */

function PackReadyRow({
  selectionHome, selectionAway, committedHome, committedAway,
  packFirstPicker, packStatus, role, catalog, onCommit,
}) {
  const sides = [
    { side: "home", label: "Home", sel: selectionHome, ready: committedHome },
    { side: "away", label: "Away", sel: selectionAway, ready: committedAway },
  ];
  const disabled = packStatus !== "picking";
  return (
    <div className="grid grid-cols-2 gap-3">
      {sides.map(({ side, label, sel, ready }) => {
        const isMe = side === role;
        const card = sel ? catalog[sel] : null;
        const first = packFirstPicker === side;
        return (
          <div key={side}
            className={`rounded-xl border p-3 ${
              ready
                ? "border-emerald-500/40 bg-emerald-500/5"
                : isMe ? "border-amber-500/40 bg-amber-500/5"
                       : "border-slate-700/50 bg-slate-900/40"
            }`}>
            <div className="flex items-baseline justify-between mb-2">
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                {label} {first && <span className="text-amber-500">· first claim</span>}
              </p>
              <p className={`text-[10px] font-bold ${
                ready ? "text-emerald-400" : sel ? "text-amber-400" : "text-slate-500"
              }`}>
                {ready ? "Ready ✓" : sel ? "Selected — not ready" : "No pick yet"}
              </p>
            </div>
            {card ? (
              <p className="text-xs font-black text-white truncate" title={card.description || card.name}>
                {card.name}
              </p>
            ) : (
              <p className="text-xs text-slate-600 italic">—</p>
            )}
            {isMe && (
              <button
                type="button"
                disabled={disabled || (!ready && !sel)}
                onClick={() => onCommit(!ready)}
                className={`mt-2 w-full py-1.5 rounded-lg text-xs font-black transition-colors
                  ${disabled || (!ready && !sel)
                    ? "bg-slate-800 text-slate-600 cursor-not-allowed"
                    : ready
                      ? "bg-slate-700 hover:bg-slate-600 text-white"
                      : "bg-amber-500 hover:bg-amber-400 text-black"}`}
              >
                {ready ? "Unready" : "Ready Up"}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─── Picks columns + Draft log ──────────────────────────────────────────── */

function PicksColumn({ label, picks, catalog, isMe }) {
  return (
    <div className={`rounded-xl border p-3 ${
      isMe ? "border-amber-500/40 bg-amber-500/5" : "border-slate-700/40 bg-slate-900/40"
    }`}>
      <div className="flex items-baseline justify-between mb-2">
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</p>
        <p className="text-xs font-black text-slate-300">{picks.length}</p>
      </div>
      {picks.length === 0 ? (
        <p className="text-[10px] text-slate-600 italic">No picks yet</p>
      ) : (
        <div className="flex flex-wrap gap-1">
          {picks.map((id, idx) => {
            const c = catalog[id] || { id, name: id, rarity: "unknown" };
            const rar = draftRarityStyle(c.rarity);
            return (
              <span key={`${id}-${idx}`}
                title={c.description || c.name}
                className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${rar.classes}`}>
                {c.name}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Chronological draft log — for pack format, groups by pack (using the
// invariant that each pack contributes one home pick + one away pick + two
// burned cards). For rotisserie, falls back to a flat alternating
// list inferred from pick order.
// Pure entry-list renderer for the draft log. Wrapped by `DraftLogDrawer`
// below with the slide-out drawer + resize chrome.
function DraftLogBody({ format, picksHome, picksAway, burned, catalog }) {
  const nameOf = (id) => catalog[id]?.name || id;
  const chipClass = (id) => {
    const c = catalog[id];
    return draftRarityStyle(c?.rarity).classes;
  };
  const pill = (id, key) => (
    <span key={key} className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${chipClass(id)}`}>
      {nameOf(id)}
    </span>
  );

  if (format === "pack") {
    const packs = Math.max(picksHome.length, picksAway.length);
    const rows = [];
    // Newest pack first — mirrors the play log's reverse-chronological feed.
    for (let p = packs - 1; p >= 0; p--) {
      const h = picksHome[p];
      const a = picksAway[p];
      const b = burned.slice(p * 2, p * 2 + 2);
      rows.push(
        <div key={p} className="border-l-2 border-slate-700 pl-3 py-1.5">
          <p className="text-[9px] font-black uppercase tracking-widest text-amber-400/80 mb-1">Pack {p + 1}</p>
          <div className="flex flex-wrap gap-1.5 items-center">
            <span className="text-[9px] font-bold text-slate-500 uppercase">H</span>
            {h ? pill(h, `h-${p}`) : <span className="text-[10px] text-slate-700 italic">—</span>}
            <span className="text-[9px] font-bold text-slate-500 uppercase ml-2">A</span>
            {a ? pill(a, `a-${p}`) : <span className="text-[10px] text-slate-700 italic">—</span>}
            {b.length > 0 && (
              <>
                <span className="text-[9px] font-bold text-red-500 uppercase ml-2 flex items-center gap-0.5">
                  <Flame size={9} /> Burned
                </span>
                {b.map((id, i) => (
                  <span key={`b-${p}-${i}`}
                    className={`text-[10px] font-bold px-1.5 py-0.5 rounded border opacity-60 line-through ${chipClass(id)}`}>
                    {nameOf(id)}
                  </span>
                ))}
              </>
            )}
          </div>
        </div>
      );
    }
    return rows.length
      ? <div className="space-y-1">{rows}</div>
      : <p className="text-[10px] text-slate-600 italic text-center py-8">No picks yet.</p>;
  }

  // Snake / rotisserie — interleave by index (home picks first by default;
  // server can flip via the coin-flip starter). Newest pick on top to match
  // the play log's reverse order.
  const maxLen = Math.max(picksHome.length, picksAway.length);
  const items = [];
  for (let i = 0; i < maxLen; i++) {
    if (picksHome[i] !== undefined) items.push({ role: "H", id: picksHome[i] });
    if (picksAway[i] !== undefined) items.push({ role: "A", id: picksAway[i] });
  }
  items.reverse();
  return items.length
    ? (
      <div className="flex flex-col gap-1">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2 px-1 py-1 rounded hover:bg-slate-800/40">
            <span className="text-[9px] font-mono font-bold px-1 py-0.5 rounded bg-slate-800 text-slate-400 leading-tight">
              {it.role}
            </span>
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${chipClass(it.id)}`}>
              {nameOf(it.id)}
            </span>
          </div>
        ))}
      </div>
    )
    : <p className="text-[10px] text-slate-600 italic text-center py-8">No picks yet.</p>;
}

// Slide-out drawer mirroring `PlayByPlayDrawer` (game). Fixed to the left
// edge of the viewport, toggle button stays visible when closed; width is
// persisted to localStorage and drag-to-resize is supported.
const DRAFT_LOG_DEFAULT_WIDTH = 280;
const DRAFT_LOG_MAX_WIDTH_RATIO = 0.5;
const DRAFT_LOG_COLLAPSE_AT = 80;
const DRAFT_LOG_LS_KEY = "junkball_draftLogWidth";

function DraftLogDrawer({ format, picksHome, picksAway, burned, catalog }) {
  const [open, setOpen] = useState(false);
  const [width, setWidth] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem(DRAFT_LOG_LS_KEY) || "", 10);
      if (!isNaN(v) && v > 0) return v;
    } catch {}
    return DRAFT_LOG_DEFAULT_WIDTH;
  });
  useEffect(() => {
    try { localStorage.setItem(DRAFT_LOG_LS_KEY, String(width)); } catch {}
  }, [width]);

  const startResize = useCallback((evt) => {
    evt.preventDefault();
    evt.stopPropagation();
    const isTouch = evt.type === "touchstart";
    const startX = isTouch ? evt.touches[0].clientX : evt.clientX;
    const startW = width;
    const maxW = Math.floor(window.innerWidth * DRAFT_LOG_MAX_WIDTH_RATIO);
    const cleanup = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
    const onMove = (e) => {
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      const delta = x - startX;
      const proposed = startW + delta;
      if (proposed < DRAFT_LOG_COLLAPSE_AT) {
        setOpen(false);
        setWidth(DRAFT_LOG_DEFAULT_WIDTH);
        cleanup();
        return;
      }
      setWidth(Math.min(maxW, proposed));
    };
    const onUp = () => cleanup();
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
  }, [width]);

  const totalPicks = picksHome.length + picksAway.length;
  const totalBurned = burned.length;
  const badgeCount = totalPicks + totalBurned;

  return (
    <div className="fixed top-0 left-0 bottom-0 z-30 flex pointer-events-none">
      {/* Drawer panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 400, damping: 35 }}
            className="relative h-full bg-slate-900/95 border-r border-slate-800 backdrop-blur-sm flex flex-col overflow-hidden pointer-events-auto"
            style={{ width }}
          >
            {/* Drag-to-resize handle on the right edge */}
            <div
              role="separator"
              aria-orientation="vertical"
              title="Drag to resize"
              onMouseDown={startResize}
              onTouchStart={startResize}
              className="absolute top-0 right-0 bottom-0 w-2 z-30 cursor-col-resize group"
            >
              <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-1 h-10 rounded-full bg-slate-700 group-hover:bg-amber-500/60 transition-colors" />
            </div>

            {/* Header */}
            <div className="flex items-baseline justify-between px-3 py-2 border-b border-slate-800 shrink-0">
              <span className="text-[10px] font-black tracking-widest text-slate-400">DRAFT LOG</span>
              <span className="text-[10px] text-slate-600">{totalPicks} picks · {totalBurned} burned</span>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-2">
              <DraftLogBody
                format={format}
                picksHome={picksHome}
                picksAway={picksAway}
                burned={burned}
                catalog={catalog}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toggle button — fixed at the left edge, sits over the drawer's
          right edge when open (matches the play log's affordance). */}
      <button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        title={open ? "Hide draft log" : "Show draft log"}
        className="self-start mt-4 ml-2 flex items-center gap-1 px-2 py-1.5 rounded-lg bg-slate-800/90 hover:bg-slate-700 border border-slate-700/60 text-slate-400 hover:text-amber-400 transition-colors backdrop-blur-sm pointer-events-auto shadow-lg"
      >
        <ScrollText size={14} />
        {!open && badgeCount > 0 && (
          <span className="text-[9px] font-bold text-amber-400 ml-0.5">{badgeCount}</span>
        )}
        {open ? <ChevronLeft size={12} /> : <ChevronRight size={12} />}
      </button>
    </div>
  );
}

/* ─── Connection dot ─────────────────────────────────────────────────────── */

function ConnectionDot({ connected, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className={`w-2 h-2 rounded-full ${connected ? "bg-emerald-400 animate-pulse" : "bg-slate-600"}`} />
      <span className={`text-[9px] font-black uppercase tracking-widest ${connected ? "text-emerald-400" : "text-slate-600"}`}>{label}</span>
      {connected ? <Wifi size={10} className="text-emerald-400" /> : <WifiOff size={10} className="text-slate-600" />}
    </div>
  );
}

/* ─── Page ───────────────────────────────────────────────────────────────── */

export default function DraftPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const gameId = params.get("game");
  const role = params.get("role") === "away" ? "away" : "home";

  const [game, setGame] = useState(null);
  // Format + meta
  const [draftFormat, setDraftFormat] = useState("rotisserie");
  const [deckSize, setDeckSize] = useState(25);
  const [draftPhase, setDraftPhase] = useState("drafting");
  // Picks state
  const [draftPool, setDraftPool] = useState([]);
  const [draftPack, setDraftPack] = useState([]);
  const [draftPicksHome, setDraftPicksHome] = useState([]);
  const [draftPicksAway, setDraftPicksAway] = useState([]);
  const [draftTurn, setDraftTurn] = useState("home");
  const [draftPackSeq, setDraftPackSeq] = useState(0);
  const [draftBurned, setDraftBurned] = useState([]);
  const [draftPackBurned, setDraftPackBurned] = useState([]);
  const [draftPackStatus, setDraftPackStatus] = useState("picking");
  const [draftPackFirstPicker, setDraftPackFirstPicker] = useState("home");
  const [packSelectionHome, setPackSelectionHome] = useState(null);
  const [packSelectionAway, setPackSelectionAway] = useState(null);
  const [packCommittedHome, setPackCommittedHome] = useState(false);
  const [packCommittedAway, setPackCommittedAway] = useState(false);
  // Last-pack picks for the burn reveal frame (server includes on finalize).
  const [lastPackPicks, setLastPackPicks] = useState(null);
  // Clock
  const [draftClockStart, setDraftClockStart] = useState(null);
  const [draftClockDuration, setDraftClockDuration] = useState(30);
  // Card catalog
  const [cardCatalog, setCardCatalog] = useState({});
  const navigatedBackRef = useRef(false);

  // Initial fetch — hydrates everything.
  useEffect(() => {
    if (!gameId) return;
    axios.get(`${API}/games/${gameId}`).then(r => {
      const g = r.data;
      setGame(g);
      if (typeof g.draft_format === "string") setDraftFormat(g.draft_format);
      if (typeof g.draft_deck_size === "number") setDeckSize(g.draft_deck_size);
      if (g.draft_phase === "drafting" || g.draft_phase === "complete") setDraftPhase(g.draft_phase);
      if (Array.isArray(g.draft_pool)) setDraftPool(g.draft_pool);
      if (Array.isArray(g.draft_pack)) setDraftPack(g.draft_pack);
      if (Array.isArray(g.draft_picks_home)) setDraftPicksHome(g.draft_picks_home);
      if (Array.isArray(g.draft_picks_away)) setDraftPicksAway(g.draft_picks_away);
      if (g.draft_turn === "home" || g.draft_turn === "away") setDraftTurn(g.draft_turn);
      if (typeof g.draft_pack_seq === "number") setDraftPackSeq(g.draft_pack_seq);
      if (Array.isArray(g.draft_burned)) setDraftBurned(g.draft_burned);
      if (Array.isArray(g.draft_pack_burned)) setDraftPackBurned(g.draft_pack_burned);
      if (typeof g.draft_pack_status === "string") setDraftPackStatus(g.draft_pack_status);
      if (g.draft_pack_first_picker === "home" || g.draft_pack_first_picker === "away") {
        setDraftPackFirstPicker(g.draft_pack_first_picker);
      }
      setPackSelectionHome(g.draft_pack_selection_home || null);
      setPackSelectionAway(g.draft_pack_selection_away || null);
      setPackCommittedHome(!!g.draft_pack_committed_home);
      setPackCommittedAway(!!g.draft_pack_committed_away);
      if (typeof g.draft_clock_started_at === "number") setDraftClockStart(g.draft_clock_started_at);
      if (typeof g.draft_clock_duration === "number") setDraftClockDuration(g.draft_clock_duration);
    }).catch(() => {});
  }, [gameId]);

  // Card catalog — one-shot fetch for chip names / effect lines.
  useEffect(() => {
    axios.get(`${API}/cards/mock`).then(r => {
      const map = {};
      (r.data || []).forEach(c => { map[c.id] = c; });
      setCardCatalog(map);
    }).catch(() => {});
  }, []);

  // WS message handler — mirrors initial-fetch hydration + handles the
  // narrower draft_state patches.
  const handleMessage = useCallback((msg) => {
    if (msg.type === "game_state") {
      const g = msg.game;
      setGame(g);
      if (typeof g.draft_format === "string") setDraftFormat(g.draft_format);
      if (typeof g.draft_deck_size === "number") setDeckSize(g.draft_deck_size);
      if (g.draft_phase === "drafting" || g.draft_phase === "complete") setDraftPhase(g.draft_phase);
      if (Array.isArray(g.draft_pool)) setDraftPool(g.draft_pool);
      if (Array.isArray(g.draft_pack)) setDraftPack(g.draft_pack);
      if (Array.isArray(g.draft_picks_home)) setDraftPicksHome(g.draft_picks_home);
      if (Array.isArray(g.draft_picks_away)) setDraftPicksAway(g.draft_picks_away);
      if (g.draft_turn === "home" || g.draft_turn === "away") setDraftTurn(g.draft_turn);
      if (typeof g.draft_pack_seq === "number") setDraftPackSeq(g.draft_pack_seq);
      if (Array.isArray(g.draft_burned)) setDraftBurned(g.draft_burned);
      if (Array.isArray(g.draft_pack_burned)) setDraftPackBurned(g.draft_pack_burned);
      if (typeof g.draft_pack_status === "string") setDraftPackStatus(g.draft_pack_status);
      if (g.draft_pack_first_picker === "home" || g.draft_pack_first_picker === "away") {
        setDraftPackFirstPicker(g.draft_pack_first_picker);
      }
      setPackSelectionHome(g.draft_pack_selection_home || null);
      setPackSelectionAway(g.draft_pack_selection_away || null);
      setPackCommittedHome(!!g.draft_pack_committed_home);
      setPackCommittedAway(!!g.draft_pack_committed_away);
      if (typeof g.draft_clock_started_at === "number") setDraftClockStart(g.draft_clock_started_at);
      if (typeof g.draft_clock_duration === "number") setDraftClockDuration(g.draft_clock_duration);
    } else if (msg.type === "draft_state") {
      if (Array.isArray(msg.draft_pool)) setDraftPool(msg.draft_pool);
      if (Array.isArray(msg.draft_pack)) setDraftPack(msg.draft_pack);
      if (Array.isArray(msg.draft_picks_home)) setDraftPicksHome(msg.draft_picks_home);
      if (Array.isArray(msg.draft_picks_away)) setDraftPicksAway(msg.draft_picks_away);
      if (msg.draft_turn === "home" || msg.draft_turn === "away") setDraftTurn(msg.draft_turn);
      if (typeof msg.draft_pack_seq === "number") setDraftPackSeq(msg.draft_pack_seq);
      if (Array.isArray(msg.draft_burned)) setDraftBurned(msg.draft_burned);
      if (Array.isArray(msg.draft_pack_burned)) setDraftPackBurned(msg.draft_pack_burned);
      if (typeof msg.draft_pack_status === "string") setDraftPackStatus(msg.draft_pack_status);
      if (msg.draft_pack_first_picker === "home" || msg.draft_pack_first_picker === "away") {
        setDraftPackFirstPicker(msg.draft_pack_first_picker);
      }
      if ("draft_pack_selection_home" in msg) setPackSelectionHome(msg.draft_pack_selection_home || null);
      if ("draft_pack_selection_away" in msg) setPackSelectionAway(msg.draft_pack_selection_away || null);
      if ("draft_pack_committed_home" in msg) setPackCommittedHome(!!msg.draft_pack_committed_home);
      if ("draft_pack_committed_away" in msg) setPackCommittedAway(!!msg.draft_pack_committed_away);
      if (msg.last_pack_picks) setLastPackPicks(msg.last_pack_picks);
      if ("draft_clock_started_at" in msg) {
        setDraftClockStart(msg.draft_clock_started_at);
      }
      if (typeof msg.draft_clock_duration === "number") setDraftClockDuration(msg.draft_clock_duration);
      if (msg.draft_phase === "complete") setDraftPhase("complete");
    } else if (msg.type === "draft_complete") {
      setDraftPhase("complete");
    }
  }, []);

  const { send: wsSend, connected, homeConnected, awayConnected } = useGameSocket(
    gameId || null, role, handleMessage
  );

  // Auto-navigate back when draft completes — gives the user a moment to
  // see the final state before bouncing. The deck-builder lobby now lives
  // inside /online-setup behind the `draft_enabled` toggle.
  useEffect(() => {
    if (draftPhase === "complete" && gameId && !navigatedBackRef.current) {
      navigatedBackRef.current = true;
      const t = setTimeout(() => {
        navigate(`/online-setup?game=${gameId}&role=${role}`, { replace: true });
      }, 1500);
      return () => clearTimeout(t);
    }
  }, [draftPhase, gameId, role, navigate]);

  // Safety: if a user lands here directly while the lobby is still in the
  // idle phase (or there's no game), bounce them back to the setup screen.
  useEffect(() => {
    if (game && game.draft_phase && game.draft_phase !== "drafting" && game.draft_phase !== "complete") {
      navigate(`/online-setup?game=${gameId}&role=${role}`, { replace: true });
    }
  }, [game, gameId, role, navigate]);

  /* Pick / commit handlers */

  const myTurn = draftTurn === role;

  const handlePoolPick = useCallback((cardId) => {
    if (draftFormat === "pack" || !myTurn) return;
    wsSend({ type: "draft_pick", card_id: cardId });
  }, [draftFormat, myTurn, wsSend]);

  const handlePackSelect = useCallback((cardId) => {
    // cardId may be null to deselect.
    if (draftFormat !== "pack") return;
    wsSend({ type: "draft_pack_select", card_id: cardId });
  }, [draftFormat, wsSend]);

  const handlePackCommit = useCallback((ready) => {
    if (draftFormat !== "pack") return;
    wsSend({ type: "draft_pack_commit", ready: !!ready });
  }, [draftFormat, wsSend]);

  /* Render guards */

  if (!gameId) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex flex-col">
        <Nav />
        <div className="flex-1 flex items-center justify-center pt-20">
          <p className="text-sm text-slate-500">Missing game id.</p>
        </div>
      </div>
    );
  }

  const fmtMeta = DRAFT_FORMATS.find(f => f.id === draftFormat) || DRAFT_FORMATS[0];
  const totalPicks = draftPicksHome.length + draftPicksAway.length;
  const headerTurnText = draftFormat === "pack"
    ? (draftPackStatus === "burn_reveal" ? "Burn reveal…" : "Both players pick simultaneously")
    : (myTurn ? "Your turn" : `${draftTurn === "home" ? "Home" : "Away"} is picking…`);
  const headerTurnTone = draftFormat === "pack" || myTurn
    ? "bg-amber-500/20 border-amber-500/40 text-amber-300"
    : "bg-slate-800 border-slate-700 text-slate-400";

  return (
    // `pt-20` clears the fixed Nav (h ≈ 56px + breathing room) — same offset
    // PvPSetup uses so the draft surface lines up with the rest of the lobby.
    <div className="min-h-screen bg-slate-950 text-white pb-12 pt-20">
      <Nav />
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div>
            <p className="text-xs font-bold text-amber-400 tracking-widest uppercase mb-1">
              Deck Builder · {fmtMeta.label} Draft
            </p>
            <h1 className="text-3xl font-black">Drafting the shared deck</h1>
            <p className="text-slate-400 text-sm mt-1.5 max-w-2xl leading-relaxed">{fmtMeta.blurb}</p>
          </div>
          <div className="flex flex-col items-start sm:items-end gap-1.5 pt-1 flex-shrink-0">
            <ConnectionDot connected={homeConnected} label="Home" />
            <ConnectionDot connected={awayConnected} label="Away" />
          </div>
        </div>

        {/* Status bar */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-5 px-1">
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
            {totalPicks} / {deckSize} picks · {draftBurned.length} burned
          </span>
          <div className="flex items-center gap-2">
            <DraftPickClock startedAt={draftClockStart} duration={draftClockDuration} />
            <span className={`text-xs font-black px-2 py-1 rounded-lg border ${headerTurnTone}`}>
              {headerTurnText}
            </span>
          </div>
        </div>

        {/* Connection nudge */}
        {!connected && (
          <div className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-2 flex items-center gap-2">
            <Loader2 size={14} className="animate-spin text-amber-400" />
            <span className="text-xs text-amber-300">Reconnecting…</span>
          </div>
        )}

        {/* Format-specific draft area */}
        {draftFormat === "pack" ? (
          <div className="space-y-4 mb-6">
            <PackBoard
              pack={draftPack}
              packSeq={draftPackSeq}
              packStatus={draftPackStatus}
              packBurned={draftPackBurned}
              lastPickPicks={lastPackPicks}
              selectionHome={packSelectionHome}
              selectionAway={packSelectionAway}
              committedHome={packCommittedHome}
              committedAway={packCommittedAway}
              role={role}
              catalog={cardCatalog}
              onSelect={handlePackSelect}
            />
            <PackReadyRow
              selectionHome={packSelectionHome}
              selectionAway={packSelectionAway}
              committedHome={packCommittedHome}
              committedAway={packCommittedAway}
              packFirstPicker={draftPackFirstPicker}
              packStatus={draftPackStatus}
              role={role}
              catalog={cardCatalog}
              onCommit={handlePackCommit}
            />
            <p className="text-[10px] text-slate-600 text-center">
              {draftPool.length} card{draftPool.length === 1 ? "" : "s"} left in the deck behind this pack.
            </p>
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5 mb-6 space-y-4">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Pool</p>
            <PoolGrid
              pool={draftPool}
              catalog={cardCatalog}
              disabled={!myTurn}
              onPick={handlePoolPick}
              grouped={draftFormat === "rotisserie"}
            />
          </div>
        )}

        {/* Picks columns */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <PicksColumn label="Home picks" picks={draftPicksHome} catalog={cardCatalog} isMe={role === "home"} />
          <PicksColumn label="Away picks" picks={draftPicksAway} catalog={cardCatalog} isMe={role === "away"} />
        </div>

        {/* Footer: complete banner */}
        <AnimatePresence>
          {draftPhase === "complete" && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mt-6 rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-5 text-center flex flex-col items-center gap-2"
            >
              <Check className="text-emerald-400" size={22} />
              <p className="text-emerald-300 font-black text-lg">Draft complete</p>
              <p className="text-slate-400 text-xs">Returning to roster creation…</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
